// src/pages/AppDownloadPage.tsx
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import longLogo from '../assets/sparki-long.png';

const detectDefaultPlatform = () => {
  if (typeof navigator === 'undefined') return 'ios';
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('android')) return 'android';
  return 'ios';
};

const AppDownloadPage: React.FC = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const garageCode = (params.get('code') || '').trim();

  const iosUrl = process.env.REACT_APP_IOS_STORE_URL || '';
  const androidUrl = process.env.REACT_APP_ANDROID_STORE_URL || '';

  // iOS default unless device looks Android
  const [platform, setPlatform] = useState<'ios' | 'android'>(detectDefaultPlatform());

  const activeLink = useMemo(
    () => (platform === 'ios' ? iosUrl : androidUrl),
    [platform, iosUrl, androidUrl]
  );
  const canDownload = Boolean(activeLink);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Download Sparki',
          url: canDownload ? activeLink : window.location.href,
        });
      } else {
        await copy(canDownload ? activeLink : window.location.href);
        alert('Link copied!');
      }
    } catch {}
  };

  return (
    <section className="container app-landing">
      <div className="app-card">
        {/* Left column */}
        <div className="app-copy">
          <img src={longLogo} alt="sparki" className="brand-long-logo" />
            <div className="brand-kicker" style={{ marginTop: '.25rem' }}>
            Your garage, on your phone
        </div>

          <h1 className="app-headline">Download our app and book in 3 clicks</h1>

          {/* Garage code — BIG & obvious */}
          {garageCode && (
            <div className="garage-code-hero" aria-live="polite">
              <small className="code-hint">
                Please enter this <b>garage code</b> when you sign up to our free app
              </small>
              <div className="code-pill code-pill-lg">{garageCode}</div>
            </div>
          )}

          <ul className="benefits">
            <li>📅 Book fast — no calls needed</li>
            <li>💬 Get an instant quote</li>
            <li>📄 View your invoice anytime</li>
            <li>🔔 Notifications when your car’s ready</li>
          </ul>

          {/* Platform toggle */}
          <div className="platform-toggle" role="tablist" aria-label="Choose your platform">
            <button
              role="tab"
              aria-selected={platform === 'ios'}
              className={`toggle-btn ${platform === 'ios' ? 'active' : ''}`}
              onClick={() => setPlatform('ios')}
            >
              iPhone
            </button>
            <button
              role="tab"
              aria-selected={platform === 'android'}
              className={`toggle-btn ${platform === 'android' ? 'active' : ''}`}
              onClick={() => setPlatform('android')}
            >
              Android
            </button>
          </div>

          {/* Store CTAs */}
          <div className="store-ctas">
            <a
              className={`store-btn ${platform}`}
              href={canDownload ? activeLink : undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!canDownload}
              onClick={(e) => {
                if (!canDownload) e.preventDefault();
              }}
            >
              {platform === 'ios' ? 'Download on the App Store' : 'Get it on Google Play'}
            </a>
            <button className="secondary-btn" type="button" onClick={share}>
              Share link
            </button>
            <button
              className="secondary-btn"
              type="button"
              onClick={() => copy(canDownload ? activeLink : window.location.href)}
            >
              Copy link
            </button>
          </div>
        </div>

        {/* Right column */}
        <div className="app-qr">
          <div className="qr-card">
            <div className="qr-label">
              Scan to {platform === 'ios' ? 'download on the App Store' : 'get it on Google Play'}
            </div>
            <div className="qr-wrap">
              <QRCodeSVG value={canDownload ? activeLink : window.location.href} size={260} includeMargin />
            </div>
            <div className="qr-fallback">
              Or visit: <span>{canDownload ? activeLink : window.location.href}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppDownloadPage;
